import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionCoordinatorService } from '../../../services/session/session-coordinator.service';
import { SessionLifecycleEmitterService } from '../../../services/session/session-lifecycle-emitter.service';
import { Gd2ToastrService } from '../../../services/ui/gd2-toastr.service';

const COUNTDOWN_TICK_MS = 1000;

@Component({
    selector: 'app-session-expiry-warning',
    templateUrl: './session-expiry-warning.component.html',
    styleUrls: ['./session-expiry-warning.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SessionExpiryWarningComponent implements OnInit, OnDestroy {
  @ViewChild('dialog', { static: true }) private readonly dialogElement: ElementRef<HTMLDialogElement>;

  hardExpiryAtEpochMs: number = Date.now();
  remainingSeconds = 0;
  isExtending = false;

  private countdownIntervalId: ReturnType<typeof setInterval> = null;
  private readonly subscriptions = new Subscription();
  private sessionExpired = false;

  constructor(
    private readonly sessionCoordinatorService: SessionCoordinatorService,
    private readonly sessionLifecycleEmitter: SessionLifecycleEmitterService,
    private readonly toaster: Gd2ToastrService,
  ) { }

  ngOnInit(): void {
    this.updateRemainingSeconds();
    this.countdownIntervalId = setInterval(() => this.updateRemainingSeconds(), COUNTDOWN_TICK_MS);

    this.subscriptions.add(
      this.sessionLifecycleEmitter.warningShown.subscribe(event => {
        if (event.type === 'cap') {
          // A cap warning firing while the idle dialog is already open must never overwrite this
          // dialog's hardExpiryAtEpochMs with the cap's own expiry time (GD2-30879 T06, BDD-10) -
          // the cap warning is surfaced separately as a toast by HeaderComponent, never in-dialog.
          return;
        }
        this.hardExpiryAtEpochMs = event.hardExpiryAtEpochMs;
        this.updateRemainingSeconds();
        this.open();
      }),
    );
    this.subscriptions.add(
      this.sessionLifecycleEmitter.sessionExtended.subscribe(() => this.close()),
    );
    this.subscriptions.add(
      this.sessionLifecycleEmitter.sessionExpired.subscribe(() => {
        this.sessionExpired = true;
        this.close();
      }),
    );

    const currentSessionState = this.sessionCoordinatorService.getSessionState();
    if (currentSessionState.warningVisible) {
      this.hardExpiryAtEpochMs = currentSessionState.lastActiveEpochMs
        + (currentSessionState.idealTimeIntervalMinutes * 60000);
      this.updateRemainingSeconds();
      this.open();
    }
  }

  ngOnDestroy(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }
    this.subscriptions.unsubscribe();
  }

  extendSession(): void {
    if (this.isExtending) {
      return;
    }
    this.isExtending = true;
    this.sessionCoordinatorService.extendSession()
      .then(refreshed => {
        if (!refreshed) {
          this.toaster.open({
            text: 'We could not renew your login. Please save your work and log in again soon.',
            type: 'danger',
            position: 'top-right',
            duration: 10000,
          });
        }
      })
      .finally(() => {
        this.isExtending = false;
      });
  }

  preventClose(event: Event): void {
    event.preventDefault();
  }

  get remainingTimeDisplay(): string {
    const minutes = Math.floor(this.remainingSeconds / 60);
    const seconds = this.remainingSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  private updateRemainingSeconds(): void {
    this.remainingSeconds = Math.max(0, Math.round((this.hardExpiryAtEpochMs - Date.now()) / 1000));
    if (this.remainingSeconds === 0 && !this.sessionExpired && this.dialogElement.nativeElement.open) {
      this.sessionExpired = true;
      this.sessionCoordinatorService.forceLocalExpiry();
    }
  }

  private open(): void {
    if (!this.dialogElement.nativeElement.open) {
      this.dialogElement.nativeElement.showModal();
    }
  }

  private close(): void {
    this.dialogElement.nativeElement.close();
  }
}
